import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RequireAccess } from '../components/RequireAccess';
import { LotCard } from '../components/LotCard';
import { Skeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { DotRule } from '../components/DotRule';
import { Disclosure } from '../components/Disclosure';
import { HeartIcon } from '../components/WatchButton';
import { Cluster, Container, Heading, Stack, Text } from '../components/ui';
import { fetchSalesRoomPosts, type SalesRoomBuckets } from '../lib/queries';
import type { Post } from '../lib/types';
import './SalesRoom.css';

/**
 * /sales-room - members-only lens over posts with the auction feature.
 *
 * Three buckets pulled in one round-trip (see fetchSalesRoomPosts):
 *   1. Live now - sorted by ends_at ascending
 *   2. Coming soon - sorted by starts_at ascending
 *   3. Recently ended - last 30 days, ends_at descending
 *
 * Each section renders only if its bucket is non-empty. When all three
 * are empty the page falls back to a single EmptyState.
 */
export default function SalesRoom({ lotHrefBase = '/p' }: { lotHrefBase?: string }) {
  return (
    <RequireAccess>
      <SalesRoomInner lotHrefBase={lotHrefBase} />
    </RequireAccess>
  );
}

function SalesRoomInner({ lotHrefBase }: { lotHrefBase: string }) {
  const [buckets, setBuckets] = useState<SalesRoomBuckets | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const data = await fetchSalesRoomPosts(ctrl.signal);
        if (ctrl.signal.aborted) return;
        setBuckets(data);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        console.error('[sales-room] fetch failed', err);
        setError(err?.message ?? 'Something broke loading the Saleroom.');
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  const isEmpty =
    !loading &&
    !error &&
    buckets !== null &&
    buckets.live.length === 0 &&
    buckets.scheduled.length === 0 &&
    buckets.ended.length === 0;

  return (
    <div data-ds className="sales-room">
      <Container as="header" size="lg" className="sales-room__header">
        <Stack gap="tight">
          <Cluster justify="between" gap="group">
            {/* .kicker set-piece eyebrow: carries the halftone-legibility shadow. */}
            <p className="kicker">Saleroom</p>
            <Link to="/favorites" className="pill-action sales-room__fav">
              <HeartIcon size={14} />
              Favorites
            </Link>
          </Cluster>
          <Heading level="h1">The Auction Closet</Heading>
          <Text role="lede" muted prose>
            In the Saleroom, you'll find distinct pieces of Converted Clothes up
            for auction. Each piece is named and listed individually as Kate
            finishes them. The bidding is in US dollars, and auctions auto-extend
            two minutes if a bid lands in the final stretch.
          </Text>
        </Stack>
        <DotRule className="sales-room__rule" />
      </Container>

      <Container size="lg">
        {loading ? (
          <SkeletonGrid />
        ) : error ? (
          <EmptyState
            eyebrow="Error"
            headline="We couldn't load the Saleroom."
            body={error}
          />
        ) : isEmpty ? (
          <EmptyState
            eyebrow="Quiet for now"
            headline="No lots in the Saleroom yet."
            body="Watch this space; pieces drop as Kate finishes them."
          />
        ) : (
          buckets && (
            <>
              <LotSection title="Live now" posts={buckets.live} lotHrefBase={lotHrefBase} />
              <LotSection title="Coming soon" posts={buckets.scheduled} lotHrefBase={lotHrefBase} />
              {/* "Sale Archive", not "Sold" - the bucket holds unsold lots too.
                  Collapsed accordion by default; opens to a tight grid of small,
                  full-colour tiles so live lots stay the hero (team, 2026-06-22). */}
              {buckets.ended.length > 0 && (
                <section className="sales-room__section sales-room__section--archive">
                  <Disclosure title="Sale Archive">
                    <div className="lot-grid lot-grid--dense">
                      {buckets.ended.map((p) => (
                        <LotCard key={p.id} post={p} hrefBase={lotHrefBase} />
                      ))}
                    </div>
                  </Disclosure>
                </section>
              )}
            </>
          )
        )}
      </Container>
    </div>
  );
}

function LotSection({ title, posts, lotHrefBase = '/p' }: { title: string; posts: Post[]; lotHrefBase?: string }) {
  if (posts.length === 0) return null;
  return (
    <section className="sales-room__section">
      <div className="sales-room__section-head">
        <Heading level="h3" className="uppercase tracking-[0.02em]">{title}</Heading>
      </div>
      <div className="lot-grid">
        {posts.map((p) => (
          <LotCard key={p.id} post={p} hrefBase={lotHrefBase} />
        ))}
      </div>
    </section>
  );
}

function SkeletonGrid() {
  return (
    <section className="sales-room__section" aria-busy="true" aria-label="Loading lots">
      <div className="lot-grid">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="sales-room__skel-cell">
            <Skeleton variant="card" aspectRatio={4 / 5} />
            <div className="sales-room__skel-meta">
              <Skeleton variant="line" width="70%" height={18} />
              <Skeleton variant="line" width="45%" height={12} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
